/**
 * Microsoft Purview as the console manages it (AIOE-only; the device-facing shapes are the mirrored
 * purview.ts and purviewApply.ts; docs/purview.md):
 *
 *   GET    /admin/v1/purview                       Settings → Microsoft Purview: the connection, labels, settings, how it has gone
 *   PUT    /admin/v1/purview                       connect (proven by reading the tenant's labels) or change the settings
 *   DELETE /admin/v1/purview                       forget it
 *   POST   /admin/v1/purview/sync                  read the tenant's labels now
 *   PUT    /admin/v1/purview/labels/:id            { sensitivity } — map one label; also adds a partner tenant's label by hand
 *   DELETE /admin/v1/purview/labels/:id            forget a partner label (a synced one comes back on the next sync)
 *   PUT    /admin/v1/purview/location              { siteUrl, folder? } — where protected files go (phase 4)
 */
import { z } from "zod";
import { hostedActionSchema, labelRouteSchema, labelRuleSchema, sensitivitySchema } from "./sensitivityLabels.js";
/**
 * The Graph application permissions, per phase, and which application holds each. Entra bars an
 * agent identity blueprint principal from file permissions ("cannot be granted to agent identity
 * blueprint principals") and Graph refuses it the labels endpoint even when the role is granted, so
 * the platform calls as the blueprint where Microsoft allows it and as the AIOE API application
 * otherwise (`either`); the SharePoint route is the API application's alone (`api`).
 */
export const PURVIEW_PERMISSIONS = [
    { name: "InformationProtectionPolicy.Read.All", app: "either", why: "read the tenant's sensitivity labels and their markings (phase 2)" },
    { name: "ProtectionScopes.Compute.User", app: "either", why: "ask whether a person's prompts are in scope of a Purview DLP policy (phase 3)" },
    { name: "Content.Process.User", app: "either", why: "Purview's verdict on a prompt or request, for the person (phase 3)" },
    { name: "ContentActivity.Write", app: "either", why: "report label and data-protection activity to Purview, metadata only (phase 3)" },
    { name: "Files.ReadWrite.All", app: "api", why: "apply an encrypting label through SharePoint and read the protected file back (phase 4; metered API). Sites.Selected with write on the chosen site does instead" },
];
export const purviewSettingsSchema = z.object({
    routes: z.partialRecord(sensitivitySchema, z.array(labelRouteSchema).min(1).max(3)).optional(),
    hostedAction: z.partialRecord(sensitivitySchema, hostedActionSchema).optional(),
    unknownLabel: sensitivitySchema.optional(),
    foreignTenant: sensitivitySchema.optional(),
    organisationProviders: z.array(z.string().min(1).max(100)).max(50).optional(),
    rules: z.array(labelRuleSchema).max(200).default([]),
    /** Phase 3: ask Purview before a prompt or request goes to a hosted model. Published as the policy's `dlp.purview`. */
    purview: z.object({ evaluate: z.enum(["off", "prompts", "all"]).default("off"), classifier: z.enum(["also", "instead"]).default("also"), failClosed: z.boolean().default(false), budgetMs: z.number().int().min(250).max(30_000).default(4_000) }).optional(),
    /** Phase 4: offer the SharePoint route for protected labels, and keep the items after the machine fetched them. */
    protectedLabels: z.object({ enabled: z.boolean().default(false), keepItems: z.boolean().default(false) }).optional(),
});
export const purviewLabelMapSchema = z.object({
    sensitivity: sensitivitySchema,
    /** For a partner tenant's label added by hand. */
    name: z.string().min(1).max(200).optional(),
    tenantId: z.string().max(64).optional(),
});
export const purviewLocationSchema = z.object({
    /** A SharePoint site, https://contoso.sharepoint.com/sites/Engineering; its default library takes the files. */
    siteUrl: z.string().url().max(500),
    /** The folder in that library (created when missing). */
    folder: z.string().min(1).max(200).default("Mojo Up artifacts"),
});
/** `POST /admin/v1/purview/try`: a prompt the administrator types, asked of Purview as a workbench would for them. Never stored. */
export const purviewTryRequestSchema = z.object({ text: z.string().min(1).max(4000) });
//# sourceMappingURL=purviewAdmin.js.map